/**
 * The plumbing shared by every non-operator marketplace category: one catalogue fetch, one
 * single-flight `busy` flag, one re-fetch-after-mutate rule, one error message.
 *
 * `shop-charms.ts`, `shop-player-cards.ts`, `shop-weapon-skins.ts` and `shop-titles.ts` each
 * hand-rolled this same load/act/redraw loop before the marketplace rebuild; `shop-sprays.ts`
 * and `shop-emotes.ts` would have been two more copies of it. Extracted once so a category
 * adapter is just "fetch this, and turn it into `MarketItem[]`" — the mutation bookkeeping
 * (busy-lock, re-fetch, refresh the header pill, catch and report) lives here instead of six
 * times over.
 *
 * A failed `fetchCatalogue()` never throws past this module: `hasFailed()` flips true and
 * `getItems()` answers `[]`, which is how a route hides one dead category without touching the
 * five live ones (§3 of the rebuild brief).
 */
import { refreshPlayerProgress } from '../player-progress-store';
import type { MarketCategoryId, MarketItem } from './marketplace-tile';

export interface MarketSection {
  id: MarketCategoryId;
  label: string;
  /**
   * One line saying what this category IS and, crucially, WHERE the player will see it.
   *
   * Every category had one written (`shop.<category>.note`, in all six locales) and only
   * `weaponSkins` ever rendered it — the rest were translated six times and shown zero times.
   * A player buying a player card was told nothing at all about what it does, which is exactly
   * what came back: "карточка игрока которую можно купить вообще не понятно что делает"
   * (Arturs, 2026-09-21). For a cosmetic, "where do I see this" IS the product.
   */
  note?: string;
  /**
   * Draws this category's whole pane, instead of the shared tile grid.
   *
   * Exactly one category needs it — Sell, which is not a catalogue of things to want but a
   * ledger of things you own, and wants sorting, a per-row calculation and multi-select that a
   * grid of buy-tiles cannot express. Optional so every other category keeps the one renderer
   * they share; a category that sets this owns its own markup and its own empty state.
   */
  renderPane?: (container: HTMLElement) => void;
  /** `[]` before the first load resolves, while a category has failed, or once its catalogue is
   * empty — the caller does not need to distinguish those three. */
  getItems(): MarketItem[];
  /** True once the first `load()` has settled, success or failure — the route waits for this
   * before deciding whether a tab has anything to show. */
  isReady(): boolean;
  hasFailed(): boolean;
  /** The last mutation's error message, or `null` — shown under this category's own grid only. */
  getFeedback(): string | null;
  /** Fires on every state change: loaded, busy toggled, feedback set/cleared. */
  subscribe(cb: () => void): () => void;
  dispose(): void;
}

export interface MarketSectionOptions<Catalogue> {
  id: MarketCategoryId;
  label: string;
  note?: string;
  renderPane?: (container: HTMLElement) => void;
  fetchCatalogue: () => Promise<Catalogue>;
  /** `act` is bound per-render so every item's `onClick` can call it directly; `busy` is this
   * category's single-flight lock (never shared across categories — a busy purchase in Charms
   * must not freeze the Sprays grid sitting next to it). */
  toItems: (catalogue: Catalogue, act: (mutate: () => Promise<unknown>) => void, busy: boolean) => MarketItem[];
}

export function createMarketSection<Catalogue>(opts: MarketSectionOptions<Catalogue>): MarketSection {
  let catalogue: Catalogue | null = null;
  let ready = false;
  let failed = false;
  let busy = false;
  let feedback: string | null = null;
  let disposed = false;
  const subscribers = new Set<() => void>();

  function notify(): void {
    if (disposed) return;
    for (const cb of subscribers) cb();
  }

  function getItems(): MarketItem[] {
    if (!catalogue || failed) return [];
    return opts.toItems(catalogue, act, busy);
  }

  function act(mutate: () => Promise<unknown>): void {
    if (busy) return;
    busy = true;
    feedback = null;
    notify();
    void (async () => {
      try {
        await mutate();
        catalogue = await opts.fetchCatalogue();
        busy = false;
        notify();
        void refreshPlayerProgress();
      } catch (error) {
        busy = false;
        if (disposed) return;
        feedback = error instanceof Error ? error.message : String(error);
        notify();
      }
    })();
  }

  async function load(): Promise<void> {
    try {
      const data = await opts.fetchCatalogue();
      if (disposed) return;
      catalogue = data;
      failed = false;
    } catch {
      failed = true;
      catalogue = null;
    } finally {
      ready = true;
      notify();
    }
  }

  void load();

  return {
    id: opts.id,
    label: opts.label,
    note: opts.note,
    renderPane: opts.renderPane,
    getItems,
    isReady: () => ready,
    hasFailed: () => failed,
    getFeedback: () => feedback,
    subscribe(cb) {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
    dispose() {
      disposed = true;
      subscribers.clear();
    },
  };
}
